import React, { useState, useEffect } from 'react';
import { Table, Modal, Form } from 'react-bootstrap';
import Button from '../Button';
import Callout from '../Callout';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';

const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || '';
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const PAYMENT_TITLE = '2025-26 Dual School Tuition';

function ParentPayments() {
  const [showModal, setShowModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('annual');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentsDue, setPaymentsDue] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Use real Stripe price IDs provided by the user
  const UPFRONT_PRICE_ID = 'price_1RmLLlQUjB1AgVplLmjt9pyg'; // $9,500 one-time
  const MONTHLY_PRICE_ID = 'price_1RnoJhQUjB1AgVplBpwbqDFp'; // $1,000/month for 10 months

  const styles = {
    container: {
      width: '100%',
      backgroundColor: '#FFFFFF',
      padding: '20px',
      marginBottom: '20px',
      textAlign: 'left',
    },
    tableContainer: { width: '100%' },
    sectionHeader: { marginTop: 32, marginBottom: 12 },
  };

  const fetchPaymentData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Token ${token}` } };
      
      // Fetch payments due and payment history in parallel
      const [paymentsDueResponse, paymentHistoryResponse] = await Promise.all([
        axios.get(`${API_ENDPOINT}/api/payments-due/`, config),
        axios.get(`${API_ENDPOINT}/api/payment-history/`, config)
      ]);
      
      setPaymentsDue(paymentsDueResponse.data);
      setPaymentHistory(paymentHistoryResponse.data);
    } catch (error) {
      console.error('Error fetching payment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setShowModal(true);
  };
  const handleCloseModal = () => {
    setShowModal(false);
    setIsProcessing(false);
  };

  const handleContinue = async () => {
    setIsProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Token ${token}` } };
      let data, stripe;
      if (selectedPlan === 'annual') {
        // One-time payment
        ({ data } = await axios.post(`${API_ENDPOINT}/api/create-upfront-checkout-session/`, { price_id: UPFRONT_PRICE_ID }, config));
        stripe = await stripePromise;
        await stripe.redirectToCheckout({ sessionId: data.sessionId });
      } else {
        // Monthly subscription (12 months, auto-cancels)
        ({ data } = await axios.post(`${API_ENDPOINT}/api/create-monthly-checkout-session/`, { price_id: MONTHLY_PRICE_ID }, config));
        stripe = await stripePromise;
        await stripe.redirectToCheckout({ sessionId: data.sessionId });
      }
      setIsProcessing(false);
      setShowModal(false);
    } catch (err) {
      alert('Failed to start payment. Please try again.');
      setIsProcessing(false);
    }
  };

  // Refresh payment data when component mounts or when returning from Stripe
  useEffect(() => {
    fetchPaymentData();
    
    // Check if we're returning from Stripe (URL might have query params)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('payment_intent') || urlParams.get('session_id')) {
      // We're returning from Stripe, refresh data after a short delay
      setTimeout(() => {
        fetchPaymentData();
      }, 2000);
    }
  }, []);

  const renderPaymentsDue = () => {
    if (loading) {
      return <tr><td colSpan="5" className="text-center">Loading...</td></tr>;
    }
    
    if (paymentsDue.length === 0) {
      return <tr><td colSpan="5" className="text-center">No upcoming payments</td></tr>;
    }
    
    return paymentsDue.map((payment) => (
      <tr key={payment.id}>
        <td>{payment.title}</td>
        <td>{payment.due_date || '--'}</td>
        <td>{payment.amount}</td>
        <td>
          {payment.payment_type === 'monthly' ? (
            <span style={{ color: '#6c757d', fontSize: '0.9em' }}>
              Auto-pay scheduled
            </span>
          ) : (
            <Button variant="primary" onClick={handleOpenModal}>Pay Now</Button>
          )}
        </td>
      </tr>
    ));
  };

  const renderPaymentHistory = () => {
    if (loading) {
      return <tr><td colSpan="4" className="text-center">Loading...</td></tr>;
    }
    
    if (paymentHistory.length === 0) {
      return <tr><td colSpan="4" className="text-center">No payment history</td></tr>;
    }
    
    return paymentHistory.map((payment) => (
      <tr key={payment.id}>
        <td>{payment.title}</td>
        <td>{payment.date}</td>
        <td>{payment.amount}</td>
        <td>{payment.status}</td>
      </tr>
    ));
  };

  return (
    <div className="page-container">
      <div style={styles.container}>
        <h1>Payments</h1>
        <Callout text={`
          Here you can view your payment history and make tuition payments securely.
          Click Pay Now to select your payment plan.
        `} />
        <h2 style={styles.sectionHeader}>Upcoming Payments</h2>
        <div style={styles.tableContainer}>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Title</th>
                <th>Due Date</th>
                <th>Amount</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {renderPaymentsDue()}
            </tbody>
          </Table>
        </div>
        <h2 style={styles.sectionHeader}>Payment History</h2>
        <div style={styles.tableContainer}>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Title</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {renderPaymentHistory()}
            </tbody>
          </Table>
        </div>
        {/* Payment Option Modal */}
        <Modal show={showModal} onHide={handleCloseModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Select Payment Option</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group>
                <Form.Label><strong>Payment Plan</strong></Form.Label>
                <div>
                  <Form.Check
                    type="radio"
                    label="One-Time Payment ($9,500 - 5% Upfront Discount)"
                    name="plan"
                    value="annual"
                    checked={selectedPlan === 'annual'}
                    onChange={() => setSelectedPlan('annual')}
                  />
                  <Form.Check
                    type="radio"
                    label="Monthly Installments ($1,000/month for 10 months)"
                    name="plan"
                    value="monthly"
                    checked={selectedPlan === 'monthly'}
                    onChange={() => setSelectedPlan('monthly')}
                  />
                </div>
                <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                  <small>
                    <strong>Important:</strong> Monthly installments are set up as auto-pay subscriptions. 
                    Your card will be automatically charged $1,000 each month for 10 months.                     
                  </small>
                </div>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} disabled={isProcessing}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleContinue} disabled={isProcessing}>
              {isProcessing ? 'Processing...' : 'Continue to Payment'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
}

export default ParentPayments; 